import {
  useQueryState,
  useQueryStates,
  parseAsString,
  parseAsStringEnum,
  parseAsArrayOf,
  parseAsInteger,
  parseAsBoolean,
  parseAsJson,
} from 'nuqs';
import type { ChartFormValues } from './schema';

// all charts page
export type SortByOption = 'name' | 'dateCreated' | 'dateModified';
export type SortOrderOption = 'asc' | 'desc';

const useChartSearch = () =>
  useQueryState('search', parseAsString.withDefault(''));

const useViewMode = () =>
  useQueryState(
    'viewMode',
    parseAsStringEnum<'table' | 'grid'>(['table', 'grid']).withDefault('grid')
  );

const useLimit = () => useQueryState('limit', parseAsInteger.withDefault(10));

const useSkip = () => useQueryState('skip', parseAsInteger.withDefault(0));

const useChartFilter = () =>
  useQueryStates({
    sortBy: parseAsStringEnum<SortByOption>([
      'name',
      'dateCreated',
      'dateModified',
    ]).withDefault('dateCreated'),
    sortOrder: parseAsStringEnum<SortOrderOption>(['asc', 'desc']).withDefault(
      'desc'
    ),
    owners: parseAsArrayOf(parseAsString).withDefault([]),
    datasources: parseAsArrayOf(parseAsString).withDefault([]),
  });

// charts create/edit page - types imported from schema
const useSyncDataFrequency = () =>
  useQueryState(
    'syncFreq',
    parseAsStringEnum<
      NonNullable<ChartFormValues['settings']['syncDataFrequency']>
    >(['daily', 'weekly', 'monthly', 'quarterly', '']).withDefault('')
  );

const useLastUpdatedFooter = () =>
  useQueryState('lastUpdated', parseAsBoolean.withDefault(false));

const useChartTitle = () =>
  useQueryState('title', parseAsString.withDefault(''));

const useStyleConfig = () => {
  const [styleConfig, setStyleConfig] = useQueryStates(
    {
      stackedBar: parseAsBoolean.withDefault(false),
      labelPosition: parseAsStringEnum<
        ChartFormValues['style']['labelPosition']
      >(['top_left', 'bottom_left', 'top_right', 'bottom_right']).withDefault(
        'bottom_right'
      ),
      labelColor: parseAsString.withDefault('#000000'),
      axis: parseAsStringEnum<ChartFormValues['style']['axis']>([
        'no',
        'y',
        'x',
        'xy',
      ]).withDefault('xy'),
      backgroundColor: parseAsString.withDefault('#FFFFFF'),
    },
    {
      urlKeys: {
        labelPosition: 'labelPos',
        backgroundColor: 'bgColor',
      },
    }
  );

  return [styleConfig, setStyleConfig] as const;
};

const useDatasets = () =>
  useQueryState(
    'datasets',
    parseAsJson<ChartFormValues['datasets']>(value => {
      if (Array.isArray(value)) {
        return value as ChartFormValues['datasets'];
      }
      return null;
    }).withDefault([])
  );

const useDateRange = () => {
  const [dateRange, setDateRange] = useQueryStates(
    {
      period: parseAsStringEnum<ChartFormValues['dateRange']['period']>([
        'this-week',
        'this-month',
        'this-quarter',
        'this-year',
        'last-week',
        'last-month',
        'last-quarter',
        'last-year',
        'custom',
      ]).withDefault('last-month'),
      start: parseAsString,
      end: parseAsString,
    },
    {
      urlKeys: {
        period: 'dateRange',
        start: 'dateStart',
        end: 'dateEnd',
      },
    }
  );

  return [dateRange, setDateRange] as const;
};

const useRemoveWatermark = () =>
  useQueryState('removeWatermark', parseAsBoolean.withDefault(false));

/**
 * Hook to clear all chart creation/edit URL parameters
 * Useful when closing modals or navigating away from chart forms
 */
const useClearChartParams = () => {
  const [, setSyncFreq] = useSyncDataFrequency();
  const [, setLastUpdated] = useLastUpdatedFooter();
  const [, setTitle] = useChartTitle();
  const [, setStyleConfig] = useStyleConfig();
  const [, setDatasets] = useDatasets();
  const [, setDateRange] = useDateRange();
  const [, setRemoveWatermark] = useRemoveWatermark();

  return () => {
    setSyncFreq('');
    setLastUpdated(false);
    setTitle('');
    setStyleConfig({
      stackedBar: false,
      labelPosition: 'bottom_right',
      labelColor: '#000000',
      axis: 'xy',
      backgroundColor: '#FFFFFF',
    });
    setDatasets([]);
    setDateRange({
      period: 'last-month',
      start: null,
      end: null,
    });
    setRemoveWatermark(false);
  };
};

export {
  useChartSearch,
  useViewMode,
  useLimit,
  useSkip,
  useChartFilter,
  useSyncDataFrequency,
  useLastUpdatedFooter,
  useChartTitle,
  useStyleConfig,
  useDatasets,
  useDateRange,
  useRemoveWatermark,
  useClearChartParams,
};
